import {CATALOGUE_BY_TYPE} from '@/ts/composables/pure/use-today-program-mapper';
import type {InterventionPreview} from '@/ts/types/dto/interventions-dto';
import type {ProgramCatalogue} from '@/ts/types/component/today-page';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

/** A program the recap selector can open */
interface RecapProgramOption {
  header: false;
  label: string;
  value: UuidDTO;
}

/**
 * A non-selectable entry opening a group: the title of the catalogue its programs belong to, or,
 * with no catalogue, a bare divider before the programs nobody categorised in Directus
 */
interface RecapCatalogueHeader {
  header: true;
  catalogue: ProgramCatalogue | null;
  value: null;
}

/** One entry of the recap selector: a program or a catalogue title */
type RecapSelectorEntry = RecapCatalogueHeader | RecapProgramOption;

/** Titled groups first, in this order; programs without a type come last, under no title */
const GROUP_ORDER: (ProgramCatalogue | null)[] = ['medical', 'prevention', null];

/**
 * The catalogue a program belongs to, or null when nobody categorised it in Directus. The backend
 * files such a program under neither group (the Today page leaves it out), so it is not guessed
 * into one here: the recap still lists it, since it is the user's, but without a title.
 */
function catalogueOf(program: InterventionPreview): ProgramCatalogue | null {
  return program.type ? CATALOGUE_BY_TYPE[program.type] ?? null : null;
}

/**
 * The recap selector's entries, grouped by catalogue: every catalogue the user has programs in gets
 * its title, so the list always says which kind of program each one is, and a catalogue without
 * programs never gets one. Programs without a type follow the titled groups after a bare divider,
 * or form a plain list when they are all the user has. Within a group the given order stays.
 */
export function buildRecapSelectorEntries(programs: InterventionPreview[]): RecapSelectorEntry[] {
  const groups = GROUP_ORDER.map((catalogue) => ({
    catalogue,
    programs: programs.filter((program) => catalogueOf(program) === catalogue),
  })).filter((group) => group.programs.length > 0);

  return groups.flatMap(({catalogue, programs: inGroup}, index) => [
    ...(catalogue !== null || index > 0 ? [{header: true, catalogue, value: null} satisfies RecapCatalogueHeader] : []),
    ...inGroup.map((program) => ({header: false, label: program.name, value: program.id} satisfies RecapProgramOption)),
  ]);
}

export type {RecapCatalogueHeader, RecapProgramOption, RecapSelectorEntry};
